import { useEffect, useState } from "react";
import { reservations } from "../../lib/api";
import { toast } from "sonner";
import { Check, X, Trash2 } from "lucide-react";

const STATUSES = ["pending", "approved", "paid_deposit", "confirmed", "declined"];

export default function AdminReservations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    reservations.list(filter ? { status: filter } : {}).then(setItems).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await reservations.updateStatus(id, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete reservation?")) return;
    await reservations.remove(id);
    toast.success("Deleted");
    load();
  };

  const exportCSV = () => {
    const headers = ["id", "full_name", "email", "phone", "date", "party_size", "table_label", "booking_type", "status", "min_spend_quoted", "created_at"];
    const rows = items.map(r => headers.map(h => JSON.stringify(r[h] ?? "")).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `onyx-reservations-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <span className="eyebrow">— Reservations</span>
          <h1 className="font-display text-4xl mt-2">Booking <span className="italic text-gold">Manager</span></h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="filter-status" value={filter} onChange={(e) => setFilter(e.target.value)} className="luxe-input !py-2 !text-sm !w-auto">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ").toUpperCase()}</option>)}
          </select>
          <button data-testid="export-csv" onClick={exportCSV} className="btn-luxe btn-luxe-ghost !py-2 !text-[10px]">Export CSV</button>
        </div>
      </div>

      {loading ? <p className="text-white/40 italic font-display">Loading…</p> : items.length === 0 ? <p className="text-white/40 italic font-display">No reservations yet.</p> : (
        <div className="border border-white/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--onyx-charcoal)] text-[10px] tracking-luxe uppercase text-white/50">
              <tr>
                <th className="text-left p-3">Guest</th>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Party</th>
                <th className="text-left p-3">Table</th>
                <th className="text-left p-3">Min Spend</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`reservation-row-${r.id}`} className="border-t border-white/5 hover:bg-[var(--onyx-charcoal)]/50">
                  <td className="p-3">
                    <div className="font-display text-lg">{r.full_name}</div>
                    <div className="text-xs text-white/40">{r.occasion || r.booking_type.replace("_", " ")}</div>
                  </td>
                  <td className="p-3 text-xs"><div>{r.email}</div><div className="text-white/40">{r.phone}</div></td>
                  <td className="p-3">{r.date}<div className="text-xs text-white/40">{r.arrival_time}</div></td>
                  <td className="p-3">{r.booking_type === "general_admission" ? `${r.ga_ticket_count} GA` : `${r.party_size} guests`}</td>
                  <td className="p-3 font-display text-base">{r.table_label || "—"}</td>
                  <td className="p-3 text-gold font-display">${r.min_spend_quoted?.toLocaleString() || "0"}</td>
                  <td className="p-3">
                    <select data-testid={`status-select-${r.id}`} value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} className="bg-transparent border border-white/15 px-2 py-1 text-[11px] uppercase tracking-luxe-tight">
                      {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button title="Approve" onClick={() => updateStatus(r.id, "approved")} className="p-2 text-gold hover:bg-gold/10"><Check size={14} /></button>
                      <button title="Decline" onClick={() => updateStatus(r.id, "declined")} className="p-2 text-white/60 hover:bg-white/5"><X size={14} /></button>
                      <button title="Delete" onClick={() => remove(r.id)} className="p-2 text-red-400 hover:bg-red-500/10"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
